import { useState, useMemo, useCallback } from 'react';
import { X, Search, User, UserMinus, ChevronDown } from 'lucide-react';
import type { Contact, SeniorityLevel, BuyingCommitteeRole } from '../../../types';
import {
  inferSeniority,
  inferDepartment,
  inferBuyingRole,
  inferOrgPlacementConfidence,
  SENIORITY_LABELS,
  BUYING_ROLE_LABELS,
} from '../../../lib/orgChartUtils';
import { useApi } from '../../../lib/useApi';
import { logAccountActivity } from '../../../lib/activityLog';

interface Props {
  accountId: string;
  companyName: string;
  existingContacts: Contact[];
  onContactAdded: (contact: Contact) => void;
  onClose: () => void;
}

const SENIORITY_OPTIONS: { value: SeniorityLevel; label: string }[] = [
  { value: '', label: 'Select seniority...' },
  { value: 'executive', label: 'Executive (C-Suite)' },
  { value: 'executive_leadership', label: 'Executive Leadership (SVP/EVP)' },
  { value: 'senior_leadership', label: 'Senior Leadership (VP)' },
  { value: 'department_leader', label: 'Department Leader (Director/Head)' },
  { value: 'team_manager', label: 'Team Manager' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
];

const BUYING_ROLE_OPTIONS: { value: BuyingCommitteeRole; label: string }[] = [
  { value: '', label: 'Select role...' },
  { value: 'economic_buyer', label: 'Economic Buyer' },
  { value: 'technical_buyer', label: 'Technical Buyer' },
  { value: 'champion', label: 'Champion' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'validator', label: 'Validator' },
];

interface FormState {
  first_name: string;
  last_name: string;
  role: string;
  email: string;
  phone: string;
  linkedin_url: string;
  organisation: string;
  location: string;
  department: string;
  seniority: SeniorityLevel;
  buying_committee_role: BuyingCommitteeRole;
  reports_to_id: string | null;
}

export default function AddContactModal({
  accountId,
  companyName,
  existingContacts,
  onContactAdded,
  onClose,
}: Props) {
  const [form, setForm] = useState<FormState>({
    first_name: '',
    last_name: '',
    role: '',
    email: '',
    phone: '',
    linkedin_url: '',
    organisation: companyName,
    location: '',
    department: '',
    seniority: '',
    buying_committee_role: '',
    reports_to_id: null,
  });
  const [manualDept, setManualDept] = useState(false);
  const [manualSeniority, setManualSeniority] = useState(false);
  const [manualBuyingRole, setManualBuyingRole] = useState(false);
  const [showManagerPicker, setShowManagerPicker] = useState(false);
  const [managerSearch, setManagerSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { apiPost } = useApi();

  const handleRoleBlur = useCallback(() => {
    if (!form.role.trim()) return;
    setForm(prev => {
      const updates: Partial<FormState> = {};
      if (!manualDept) updates.department = inferDepartment(prev.role);
      if (!manualSeniority) updates.seniority = inferSeniority(prev.role).seniority as SeniorityLevel;
      if (!manualBuyingRole) updates.buying_committee_role = inferBuyingRole(prev.role) as BuyingCommitteeRole;
      return { ...prev, ...updates };
    });
  }, [form.role, manualDept, manualSeniority, manualBuyingRole]);

  const managerCandidates = useMemo(() => {
    const q = managerSearch.toLowerCase().trim();
    return existingContacts
      .filter(c => {
        if (!q) return true;
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        return (
          fullName.includes(q) ||
          c.role.toLowerCase().includes(q) ||
          c.department.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.org_level - b.org_level);
  }, [existingContacts, managerSearch]);

  const selectedManager = form.reports_to_id
    ? existingContacts.find(c => c.id === form.reports_to_id)
    : null;

  const handleField = (field: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'department') setManualDept(true);
    if (field === 'seniority') setManualSeniority(true);
    if (field === 'buying_committee_role') setManualBuyingRole(true);
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (!form.role.trim()) {
      setError('Job title / role is required.');
      return;
    }

    setSaving(true);
    setError('');

    const { seniority: inferredSeniority, level } = inferSeniority(form.role);
    const finalSeniority = form.seniority || inferredSeniority;
    const finalDepartment = form.department || inferDepartment(form.role);
    const finalBuyingRole = form.buying_committee_role || inferBuyingRole(form.role);

    const newContact: Omit<Contact, 'id' | 'created_at' | 'updated_at'> = {
      account_id: accountId,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      role: form.role.trim(),
      organisation: form.organisation.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      department: finalDepartment,
      seniority: finalSeniority as SeniorityLevel,
      linkedin_url: form.linkedin_url.trim(),
      location: form.location.trim(),
      buying_committee_role: finalBuyingRole as BuyingCommitteeRole,
      persona_match: '',
      persona_match_score: 0,
      use_case_relevance_score: 0,
      org_level: level,
      reports_to_id: form.reports_to_id,
      org_placement_confidence: 0,
      notes: '',
      source: 'manual',
      department_id: null,
      is_inferred: false,
    };

    const confidence = inferOrgPlacementConfidence(newContact as Contact);
    (newContact as any).org_placement_confidence = confidence;

    try {
      const { contact } = await apiPost<{ contact: Contact }>(
        `/v1/account/${accountId}/contacts`,
        newContact,
      );
      logAccountActivity(accountId, 'contact_added', `Added contact: ${form.first_name.trim()} ${form.last_name.trim()}`);
      onContactAdded(contact);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact.');
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 text-sm bg-app-canvas border border-app-line rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 transition-colors';
  const labelClass = 'block text-xs font-medium text-app-muted mb-1';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-app-surface rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-app-line">
          <div>
            <h3 className="text-sm font-semibold text-app-ink">Add Contact</h3>
            <p className="text-xs text-app-muted mt-0.5">Manually add a single contact to the org chart</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-app-muted hover:text-app-muted rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>First Name *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={e => handleField('first_name', e.target.value)}
                className={inputClass}
                placeholder="John"
              />
            </div>
            <div>
              <label className={labelClass}>Last Name *</label>
              <input
                type="text"
                value={form.last_name}
                onChange={e => handleField('last_name', e.target.value)}
                className={inputClass}
                placeholder="Smith"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Job Title / Role *</label>
            <input
              type="text"
              value={form.role}
              onChange={e => handleField('role', e.target.value)}
              onBlur={handleRoleBlur}
              className={inputClass}
              placeholder="VP of Engineering"
            />
            <p className="text-[10px] text-app-muted mt-1">
              Department, seniority, and buying role will be auto-inferred from the title
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => handleField('email', e.target.value)}
                className={inputClass}
                placeholder="john@company.com"
              />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => handleField('phone', e.target.value)}
                className={inputClass}
                placeholder="+1 555 123 4567"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>LinkedIn URL</label>
            <input
              type="url"
              value={form.linkedin_url}
              onChange={e => handleField('linkedin_url', e.target.value)}
              className={inputClass}
              placeholder="https://linkedin.com/in/..."
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Organisation</label>
              <input
                type="text"
                value={form.organisation}
                onChange={e => handleField('organisation', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input
                type="text"
                value={form.location}
                onChange={e => handleField('location', e.target.value)}
                className={inputClass}
                placeholder="London, UK"
              />
            </div>
          </div>

          <div className="border-t border-app-line pt-4">
            <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-3">Org Position</p>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Department</label>
                <input
                  type="text"
                  value={form.department}
                  onChange={e => handleField('department', e.target.value)}
                  className={inputClass}
                  placeholder="Auto-inferred from title"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Seniority</label>
                  <div className="relative">
                    <select
                      value={form.seniority}
                      onChange={e => handleField('seniority', e.target.value)}
                      className={`${inputClass} appearance-none pr-8`}
                    >
                      {SENIORITY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Buying Role</label>
                  <div className="relative">
                    <select
                      value={form.buying_committee_role}
                      onChange={e => handleField('buying_committee_role', e.target.value)}
                      className={`${inputClass} appearance-none pr-8`}
                    >
                      {BUYING_ROLE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted pointer-events-none" />
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>Reports To</label>
                {!showManagerPicker ? (
                  <button
                    type="button"
                    onClick={() => setShowManagerPicker(true)}
                    className={`${inputClass} text-left flex items-center justify-between`}
                  >
                    {selectedManager ? (
                      <span className="text-app-ink">
                        {selectedManager.first_name} {selectedManager.last_name}
                        <span className="text-app-muted ml-1.5">{selectedManager.role}</span>
                      </span>
                    ) : (
                      <span className="text-app-muted">No manager (top level)</span>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 text-app-muted" />
                  </button>
                ) : (
                  <div className="border border-app-line rounded-lg overflow-hidden bg-app-surface">
                    <div className="px-3 py-2 border-b border-app-line">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-muted" />
                        <input
                          type="text"
                          value={managerSearch}
                          onChange={e => setManagerSearch(e.target.value)}
                          placeholder="Search contacts..."
                          className="w-full pl-8 pr-3 py-1.5 text-sm bg-app-canvas border border-app-line rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-300"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-40 overflow-y-auto divide-y divide-app-line">
                      <button
                        type="button"
                        onClick={() => {
                          setForm(prev => ({ ...prev, reports_to_id: null }));
                          setShowManagerPicker(false);
                          setManagerSearch('');
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-app-canvas transition-colors ${
                          !form.reports_to_id ? 'bg-forest-50' : ''
                        }`}
                      >
                        <div className="w-6 h-6 rounded-md bg-app-canvas flex items-center justify-center flex-shrink-0">
                          <UserMinus className="w-3 h-3 text-app-muted" />
                        </div>
                        <span className="text-xs font-medium text-app-ink">No Manager (Top Level)</span>
                      </button>
                      {managerCandidates.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setForm(prev => ({ ...prev, reports_to_id: c.id }));
                            setShowManagerPicker(false);
                            setManagerSearch('');
                          }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-app-canvas transition-colors ${
                            form.reports_to_id === c.id ? 'bg-forest-50' : ''
                          }`}
                        >
                          <div className="w-6 h-6 rounded-md bg-app-canvas flex items-center justify-center flex-shrink-0">
                            <User className="w-3 h-3 text-app-muted" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-app-ink truncate">
                              {c.first_name} {c.last_name}
                            </p>
                            <p className="text-[10px] text-app-muted truncate">{c.role}</p>
                          </div>
                          {c.seniority && (
                            <span className="text-[10px] text-app-muted flex-shrink-0">
                              {SENIORITY_LABELS[c.seniority]?.split('(')[0]?.trim()}
                            </span>
                          )}
                        </button>
                      ))}
                      {managerCandidates.length === 0 && managerSearch && (
                        <div className="px-3 py-4 text-center">
                          <p className="text-xs text-app-muted">No contacts match "{managerSearch}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-app-muted mt-1">
                  Leave this empty if unknown. The contact will appear as a standalone node.
                </p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-5 py-2 bg-app-critical/10 border-t border-app-critical">
            <p className="text-xs text-app-critical">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-app-line">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-app-muted hover:text-app-ink transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary !py-2 !px-5 !text-sm"
          >
            {saving ? 'Adding...' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
